import { entryLocalDay } from "./chronicle";
import {
  isJust,
  isNothing,
  just,
  mapMaybe,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "./maybe";
import { trimmedMaybe } from "./trimmedString";
import type { ZwGoals } from "./zwConfig";

export type GoalKind = "session" | "daily" | "project";

export type GoalsProgress = {
  session: number;
  daily: number;
  project: number;
};

/** Minimal chronicle shape needed to attribute Session/Today progress. */
export type GoalsChronicleEntry = {
  author: Maybe<string>;
  timestamp: string;
  wordsDiff: Maybe<number>;
};

/** Wire chronicle → app GoalsChronicleEntry (`null` at the edge → Maybe). */
export function toGoalsChronicleEntry(raw: {
  author: unknown;
  timestamp: string;
  wordsDiff: unknown;
}): GoalsChronicleEntry {
  return {
    author: trimmedMaybe(typeof raw.author === "string" ? raw.author : ""),
    timestamp: raw.timestamp,
    wordsDiff:
      typeof raw.wordsDiff === "number" ? just(raw.wordsDiff) : nothing,
  };
}

export function normalizeGoalsAuthor(email: Maybe<string>): Maybe<string> {
  return matchMaybe(email, {
    just: (raw) => {
      const trimmed = raw.trim().toLowerCase();
      return trimmed.length > 0 ? just(trimmed) : nothing;
    },
    nothing: () => nothing,
  });
}

/**
 * Whether a chronicle entry counts toward the signed-in user's goals.
 * Matching author always counts. Unauthored legacy entries count only when we
 * have an identity (solo-era commits before share emails were recorded). When
 * we have no identity, only unauthored entries count so a stranger's named
 * work never credits an anonymous session.
 */
export function entryMatchesGoalsAuthor(
  entryAuthor: Maybe<string>,
  authorEmail: Maybe<string>,
): boolean {
  const entry = normalizeGoalsAuthor(entryAuthor);
  if (isNothing(authorEmail) || authorEmail.value.length === 0) {
    return isNothing(entry);
  }
  if (isNothing(entry)) return true;
  return entry.value === authorEmail.value;
}

/**
 * Net words from chronicle for Session (since session start) and Today
 * (local calendar day), filtered to one author. Floors each window at 0.
 */
export function sumAuthorChronicleWords(
  entries: readonly GoalsChronicleEntry[],
  authorEmail: Maybe<string>,
  opts: {
    sessionStartedAtUnix: Maybe<number>;
    todayLocal: string;
  },
): { session: number; daily: number } {
  let session = 0;
  let daily = 0;
  const sessionStartMs = mapMaybe(
    opts.sessionStartedAtUnix,
    (unix) => unix * 1000,
  );

  for (const entry of entries) {
    if (entryMatchesGoalsAuthor(entry.author, authorEmail) === false) {
      continue;
    }
    const diff = matchMaybe(entry.wordsDiff, {
      just: (n) => n,
      nothing: () => 0,
    });
    if (diff === 0) continue;

    if (entryLocalDay(entry.timestamp) === opts.todayLocal) {
      daily += diff;
    }
    if (isJust(sessionStartMs)) {
      const t = Date.parse(entry.timestamp);
      if (Number.isNaN(t) === false && t >= sessionStartMs.value) {
        session += diff;
      }
    }
  }

  return {
    session: Math.max(0, session),
    daily: Math.max(0, daily),
  };
}

export function localDateString(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Local midnight as unix seconds (device timezone). */
export function localMidnightUnix(date = new Date()): number {
  const midnight = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  return Math.floor(midnight.getTime() / 1000);
}

/** Parse a settings draft field into a positive word target, if any. */
export function parseGoalDraft(raw: string): Maybe<number> {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return nothing;
  const n = Number(trimmed.replace(/,/g, ""));
  if (Number.isFinite(n) === false || n <= 0) return nothing;
  return just(Math.floor(n));
}

/** Treat absent / 0 / negative as "no goal". */
export function goalTarget(value: Maybe<number>): Maybe<number> {
  return matchMaybe(value, {
    nothing: () => nothing,
    just: (n) => {
      if (Number.isFinite(n) === false || n <= 0) return nothing;
      return just(Math.floor(n));
    },
  });
}

export function primaryGoalKind(goals: Maybe<ZwGoals>): Maybe<GoalKind> {
  return matchMaybe(goals, {
    nothing: () => nothing,
    just: (g) => {
      if (isJust(goalTarget(toMaybe(g.sessionWords)))) return just("session");
      if (isJust(goalTarget(toMaybe(g.dailyWords)))) return just("daily");
      if (isJust(goalTarget(toMaybe(g.projectWords)))) return just("project");
      return nothing;
    },
  });
}

export function goalKindLabel(kind: GoalKind): string {
  if (kind === "session") return "Session";
  if (kind === "daily") return "Today";
  return "Book";
}

/** Words written since a git baseline (`live - baseline`, floored at 0). */
export function wordsSince(
  liveManuscriptTotal: number,
  baselineWords: Maybe<number>,
): number {
  return matchMaybe(baselineWords, {
    nothing: () => 0,
    just: (baseline) => {
      if (Number.isFinite(baseline) === false) return 0;
      return Math.max(0, liveManuscriptTotal - baseline);
    },
  });
}

export function progressForKind(
  kind: GoalKind,
  progress: GoalsProgress,
): number {
  if (kind === "session") return progress.session;
  if (kind === "daily") return progress.daily;
  return progress.project;
}

export function formatGoalProgress(current: number, target: number): string {
  return `${current.toLocaleString()} / ${target.toLocaleString()}`;
}

export function formatPrimaryGoalChip(
  kind: GoalKind,
  current: number,
  target: number,
): string {
  return `${goalKindLabel(kind)} ${formatGoalProgress(current, target)}`;
}

export function goalsTooltipLines(
  goals: ZwGoals,
  progress: GoalsProgress,
): string {
  const lines: string[] = [];
  matchMaybe(goalTarget(toMaybe(goals.sessionWords)), {
    nothing: () => {},
    just: (sessionTarget) => {
      lines.push(
        `Session ${formatGoalProgress(progress.session, sessionTarget)}`,
      );
    },
  });
  matchMaybe(goalTarget(toMaybe(goals.dailyWords)), {
    nothing: () => {},
    just: (dailyTarget) => {
      lines.push(`Today ${formatGoalProgress(progress.daily, dailyTarget)}`);
    },
  });
  matchMaybe(goalTarget(toMaybe(goals.projectWords)), {
    nothing: () => {},
    just: (projectTarget) => {
      lines.push(`Book ${formatGoalProgress(progress.project, projectTarget)}`);
    },
  });
  return lines.join("\n");
}
